import { useCallback, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0Icon } from '@factorialco/f0-react'
import { F0Select } from '@factorialco/f0-react'
import { OneModal } from '@factorialco/f0-react/dist/experimental'
import {
  Megaphone,
  Microphone,
  MicrophoneNegative,
  Settings,
  SolidPause,
  SolidPlay,
} from '@factorialco/f0-react/icons/app'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'
import {
  useAudioWaveform,
  useLocalParticipant,
  useMediaDeviceSelect,
  usePersistentUserChoices,
} from '@livekit/components-react'
import { Track } from 'livekit-client'

import i18n from 'modules/core/lib/i18n'

import * as styles from './index.vanilla.css'

interface RecordSpeakingHandlerProps {
  onEndMeeting: () => void
}

export const RecordSpeakingHandler = ({ onEndMeeting }: RecordSpeakingHandlerProps) => {
  const { userChoices } = usePersistentUserChoices()
  const { localParticipant } = useLocalParticipant()
  const [openSettings, setOpenSettings] = useState(false)

  const [audioEnabled, setAudioEnabled] = useState(userChoices.audioEnabled)

  const handleMicToggle = useCallback(async () => {
    try {
      const newState = !audioEnabled
      await localParticipant.setMicrophoneEnabled(newState)
      setAudioEnabled(newState)
    } catch (error) {}
  }, [audioEnabled, localParticipant])

  const {
    devices: audioInputDevices,
    activeDeviceId: activeAudioInputId,
    setActiveMediaDevice: setActiveAudioDevice,
  } = useMediaDeviceSelect({ kind: 'audioinput' })

  const micTrack = localParticipant?.getTrackPublication(Track.Source.Microphone)
  const micRef: TrackReferenceOrPlaceholder = {
    participant: localParticipant,
    source: Track.Source.Microphone,
    publication: micTrack,
  }

  const audioWaveform = useAudioWaveform(micRef, {
    barCount: 4,
    volMultiplier: 12,
    updateInterval: 100,
  })

  const primaryAudioLevel = audioWaveform.bars[0] || 0
  const secondaryAudioLevel = audioWaveform.bars[1] || 0
  const tertiaryAudioLevel = audioWaveform.bars[2] || 0
  const quaternaryAudioLevel = audioWaveform.bars[3] || 0

  const primaryScale = Math.min(2, Math.max(0.1, primaryAudioLevel))
  const secondaryScale = Math.min(2, Math.max(0.1, secondaryAudioLevel))
  const tertiaryScale = Math.min(2, Math.max(0.1, tertiaryAudioLevel))
  const quaternaryScale = Math.min(2, Math.max(0.1, quaternaryAudioLevel))

  const handleAudioInputChange = useCallback(
    async (deviceId: string) => {
      try {
        if (setActiveAudioDevice) {
          await setActiveAudioDevice(deviceId)
          if (!localParticipant.isMicrophoneEnabled) {
            await localParticipant.setMicrophoneEnabled(true)
          }
        }
      } catch (error) {}
    },
    [localParticipant, setActiveAudioDevice]
  )

  return (
    <>
      <Box
        position="absolute"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: '10',
        }}
        flexDirection="row"
        alignItems="center"
        gap="s12"
      >
        <F0Icon
          icon={audioEnabled ? Microphone : MicrophoneNegative}
          size="lg"
          color="#fff"
          state="animate"
        />
      </Box>

      {audioEnabled && (
        <>
          <Box
            position="absolute"
            background="grey000White"
            className={styles.speakingCircle}
            style={{
              transform: `translate(-50%, -50%) scale(${primaryScale})`,
              opacity: `${Math.max(0.1, primaryAudioLevel * 0.1)}`,
            }}
          />

          <Box
            position="absolute"
            background="grey000White"
            className={styles.speakingCircleSecondary}
            style={{
              transform: `translate(-50%, -50%) scale(${secondaryScale})`,
              opacity: `${Math.max(0.08, secondaryAudioLevel * 0.1)}`,
            }}
          />

          <Box
            position="absolute"
            background="grey000White"
            className={styles.speakingCircleTertiary}
            style={{
              transform: `translate(-50%, -50%) scale(${tertiaryScale})`,
              opacity: `${Math.max(0.06, tertiaryAudioLevel * 0.1)}`,
            }}
          />

          <Box
            position="absolute"
            background="grey000White"
            className={styles.speakingCircleQuaternary}
            style={{
              transform: `translate(-50%, -50%) scale(${quaternaryScale})`,
              opacity: `${Math.max(0.04, quaternaryAudioLevel * 0.1)}`,
            }}
          />
        </>
      )}

      <Box
        position="absolute"
        flexDirection="row"
        gap="s8"
        alignItems="center"
        background="grey000White"
        padding="s8"
        borderRadius={{ all: 'abs016' }}
        boxShadow="s50"
        style={{
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <F0Button
          label={
            audioEnabled
              ? i18n.t('meetings.record.start_record')
              : i18n.t('meetings.record.stop_record')
          }
          variant="ghost"
          onClick={() => handleMicToggle()}
          hideLabel
          icon={audioEnabled ? SolidPause : SolidPlay}
        />
        <F0Button
          label={i18n.t('meetings.record.settings_button')}
          variant="ghost"
          onClick={() => setOpenSettings(true)}
          hideLabel
          icon={Settings}
        />
        <Box
          background="grey400"
          style={{
            width: '1px',
            height: '16px',
            margin: '0 8px',
          }}
          borderRadius={{ all: 'abs008' }}
        />
        <F0Button
          label={i18n.t('meetings.record.end_meeting_button')}
          variant="default"
          onClick={onEndMeeting}
        />
      </Box>

      <OneModal isOpen={openSettings} onClose={() => setOpenSettings(false)}>
        <OneModal.Header title={i18n.t('meetings.room.settings.title')} />
        <OneModal.Content withPadding>
          <F0Select
            size="md"
            hideLabel
            onChange={handleAudioInputChange}
            options={audioInputDevices.map((device) => ({
              label:
                device.label ||
                `${i18n.t(
                  'meetings.room.control_bar.microphone.label'
                )} ${device.deviceId.substring(0, 5)}...`,
              value: device.deviceId,
            }))}
            placeholder={i18n.t('meetings.room.control_bar.microphone.placeholder')}
            searchBoxPlaceholder={i18n.t('meetings.room.control_bar.microphone.search_placeholder')}
            searchEmptyMessage={i18n.t('meetings.room.control_bar.microphone.no_microphones_found')}
            value={activeAudioInputId || ''}
            icon={Megaphone}
            label={i18n.t('meetings.room.settings.audio_input')}
          />
        </OneModal.Content>
      </OneModal>
    </>
  )
}
